import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseAdminListPageOptions<TItem, TDeleteId> {
  /** React Query cache key for the list query. */
  queryKey: readonly unknown[];
  /** Function that fetches the list data. */
  queryFn: () => Promise<{ data: TItem[] }>;
  /** Function that deletes an item. */
  deleteFn: (id: TDeleteId) => Promise<void>;
  /**
   * Extracts the delete identifier from an item.
   * For example `(tenant) => tenant.slug` or `(user) => user.id`.
   */
  getDeleteId: (item: TItem) => TDeleteId;
  /**
   * Optional additional query keys to invalidate after a successful delete.
   * Useful when a child list deletion should refresh a parent detail view.
   */
  extraInvalidateKeys?: readonly (readonly unknown[])[];
  /**
   * Optional transform applied to the raw data array after fetching.
   * For example, sorting by priority.
   */
  transformData?: (items: TItem[]) => TItem[];
}

export interface UseAdminListPageReturn<TItem> {
  /** The fetched (and optionally transformed) list of items. */
  items: TItem[];
  /** Whether the list query is currently loading. */
  isLoading: boolean;
  /** The list query error, if any. */
  error: Error | null;
  /** The item currently targeted for deletion, or null. */
  deleteTarget: TItem | null;
  /** Opens the delete confirmation modal for the given item. */
  setDeleteTarget: (item: TItem | null) => void;
  /** Whether the delete mutation is currently in flight. */
  isDeleting: boolean;
  /** Whether the delete mutation errored. */
  isDeleteError: boolean;
  /** The delete mutation error, if any. */
  deleteError: Error | null;
  /** Executes the delete mutation for the current deleteTarget. */
  confirmDelete: () => void;
  /** Cancels the delete operation: closes modal and resets mutation state. */
  cancelDelete: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAdminListPage<TItem, TDeleteId = string>(
  options: UseAdminListPageOptions<TItem, TDeleteId>,
): UseAdminListPageReturn<TItem> {
  const {
    queryKey,
    queryFn,
    deleteFn,
    getDeleteId,
    extraInvalidateKeys,
    transformData,
  } = options;

  const queryClient = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<TItem | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: TDeleteId) => deleteFn(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      if (extraInvalidateKeys) {
        for (const key of extraInvalidateKeys) {
          void queryClient.invalidateQueries({ queryKey: key });
        }
      }
      setDeleteTarget(null);
    },
  });

  const rawItems = data?.data ?? [];
  const items = transformData ? transformData(rawItems) : rawItems;

  const confirmDelete = () => {
    /* v8 ignore start */
    if (deleteTarget) {
      deleteMutation.mutate(getDeleteId(deleteTarget));
    }
    /* v8 ignore stop */
  };

  const cancelDelete = () => {
    setDeleteTarget(null);
    deleteMutation.reset();
  };

  return {
    items,
    isLoading,
    error: error ?? null,
    deleteTarget,
    setDeleteTarget,
    isDeleting: deleteMutation.isPending,
    isDeleteError: deleteMutation.isError,
    deleteError: deleteMutation.error ?? null,
    confirmDelete,
    cancelDelete,
  };
}
